import React from "react";
import ReactDOM from "react-dom";
import errorIcon from "../../assets/images/error.png";
import { useTranslation } from "react-i18next";
import Button from "../ui/Button";
import Icon from "../common/Icon";
import studio from "../../components/modals/StudioModal.module.css";

interface TextGenerationStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    status: "waiting" | "success" | "error";
    rank: number | null;
    errorTitle?: string;
    errorSubtitle?: string;
    /** When provided, a cancel button is shown while the generation is waiting. */
    onCancel?: () => void;
    cancelling?: boolean;
}

const AIGenerationStatusModal: React.FC<TextGenerationStatusModalProps> = ({
    isOpen,
    onClose,
    status,
    rank,
    errorTitle,
    errorSubtitle,
    onCancel,
    cancelling = false,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    if (!isOpen) return null;

    const getStatusContent = () => {
        switch (status) {
            case "waiting":
                return {
                    title: t("generationInProgress"),
                    subtitle: rank !== null && rank > 0 ? t("waitingInQueue", { rank }) : t("pleaseWaitTextGeneration"),
                };
            case "success":
                return {
                    title: t("generationSuccessful"),
                    subtitle: t("generationSuccessSubtitle"),
                };
            case "error":
                return {
                    title: errorTitle ?? t("generationFailed"),
                    subtitle: errorSubtitle ?? t("pleaseTryAgainLater"),
                };
            default:
                return {
                    title: "",
                    subtitle: "",
                };
        }
    };

    const { title, subtitle } = getStatusContent();

    const modal = (
        <div className={studio.overlayTop}>
            <div className={studio.generationContent}>
                {status === "waiting" && <div className={studio.generationSpinnerWrap} aria-hidden />}
                {status === "success" && (
                    <div className={studio.okBadge} aria-hidden>
                        <Icon name="ic-check" className={`ic ${studio.okBadgeIcon}`} />
                    </div>
                )}
                {status === "error" && (
                    <div className={studio.logoWrap}>
                        <img src={errorIcon} className="error" alt="" style={{ width: 100, height: 100 }} />
                    </div>
                )}
                <h2 className={studio.generationTitle}>{title}</h2>
                <p className={studio.generationSubtitle}>{subtitle}</p>
                {status === "waiting" && (
                    <div className={studio.generationProgress} aria-hidden>
                        <i className={studio.generationProgressIndicator} />
                    </div>
                )}
                {status === "waiting" && onCancel && (
                    <div className={`${studio.generationActions} ${studio.generationCancelActions}`}>
                        <Button
                            variant="dangerOutline"
                            shape="pill"
                            label={cancelling ? t("cancelingGeneration") : t("cancelGeneration")}
                            onClick={onCancel}
                            disabled={cancelling}
                        />
                    </div>
                )}
                {(status === "success" || status === "error") && (
                    <div className={studio.generationActions}>
                        <Button
                            variant="primary"
                            shape="pill"
                            label={status === "success" ? t("continue") : t("close")}
                            onClick={onClose}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default AIGenerationStatusModal;
